import React, { memo, useMemo, useEffect, useRef } from "react";
import { Container } from "@pixi/react";
import { useDispatch } from "react-redux";
import { Settings, GAME_LAYOUT } from "../../settings";
import PotCollectionOverlaySymbol from "./PotCollectionOverlaySymbol";
import { useReelRefs } from "../../Contexts/ReelRefsContext";
import { reportPotCollectionOverlayReelReady } from "../../State/actions";

/**
 * PotCollectionOverlayReelSet
 *
 * FLAT container for all Pot Collection overlay symbols.
 * All 15 symbols (5 reels × 3 rows) are siblings in ONE sortable container,
 * enabling cross-reel zIndex control — Zeus coins render on top of adjacent symbols.
 *
 * Backward-compatible: exposes the same ref API as the old per-reel structure:
 *   overlayReelRefs.current[reelIndex].getSymbolRef(symbolIndex)
 * via lightweight shim objects that delegate to the flat symbol array.
 */
const PotCollectionOverlayReelSet = memo(
  ({ symbolWidth, symbolHeight, reelSpacing }) => {
    const dispatch = useDispatch();
    const { potCollectionOverlayReelRefs } = useReelRefs();
    const symbolRefs = useRef(
      Array.from({ length: GAME_LAYOUT.REEL_COUNT }, () =>
        new Array(GAME_LAYOUT.SYMBOLS_VISIBLE)
      )
    );
    const hasReportedReady = useRef(false);

    const containerY = useMemo(
      () => -symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5),
      [symbolHeight],
    );

    // Build reel shim objects — same API as old PotCollectionOverlayReel refs
    useEffect(() => {
      if (!potCollectionOverlayReelRefs.current) return;
      for (let ri = 0; ri < GAME_LAYOUT.REEL_COUNT; ri++) {
        const reelIndex = ri; // Capture for closure
        potCollectionOverlayReelRefs.current[reelIndex] = {
          getSymbolRef: (symbolIndex) => symbolRefs.current[reelIndex][symbolIndex],
        };
      }

      // Fire ready 5 times to match the old per-reel pattern (ready gate counts to 5)
      if (!hasReportedReady.current) {
        hasReportedReady.current = true;
        for (let i = 0; i < GAME_LAYOUT.REEL_COUNT; i++) {
          dispatch(reportPotCollectionOverlayReelReady());
        }
      }
    }, [dispatch, potCollectionOverlayReelRefs]);

    // Pre-compute all 15 symbol positions
    const symbolPositions = useMemo(() => {
      const positions = [];
      const centerOffset = (GAME_LAYOUT.REEL_COUNT - 1) / 2;
      for (let ri = 0; ri < GAME_LAYOUT.REEL_COUNT; ri++) {
        for (let si = 0; si < GAME_LAYOUT.SYMBOLS_VISIBLE; si++) {
          positions.push({
            reelIndex: ri,
            symbolIndex: si,
            x: reelSpacing * (ri - centerOffset),
            y: symbolHeight * si,
          });
        }
      }
      return positions;
    }, [reelSpacing, symbolHeight]);

    return (
      <Container
        name="pot_collection_overlay"
        zIndex={9}
        y={containerY}
        sortableChildren
      >
        {symbolPositions.map(({ reelIndex, symbolIndex, x, y }) => (
          <PotCollectionOverlaySymbol
            key={`${reelIndex}_${symbolIndex}`}
            index={symbolIndex}
            reelIndex={reelIndex}
            x={x}
            y={y}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            ref={(el) => {
              if (symbolRefs.current[reelIndex]) {
                symbolRefs.current[reelIndex][symbolIndex] = el;
              }
            }}
          />
        ))}
      </Container>
    );
  },
);

PotCollectionOverlayReelSet.displayName = "PotCollectionOverlayReelSet";

export default PotCollectionOverlayReelSet;
